"use client";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { FaShareAlt, FaStar } from "react-icons/fa";
import { LuCalendar } from "react-icons/lu";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { CiMoneyBill } from "react-icons/ci";
import { formatCurrency } from "@/utils/currencyFormatter";
import { scaleImageUrl } from "@/utils/scaleImage";
import { calculateActivityFee } from "@/utils/calculateActivityFee";
import { formatDateWithoutDay } from "@/utils/formatDate";
import { BsFillPatchCheckFill } from "react-icons/bs";
import { getActivityFee } from "@/utils/getActivityFee";

export interface ActivityCardProps {
  isPast?: boolean;
  isCategoryPage?: boolean;
  activity: Activity;
}

export default function GuidedActivityCard({
  isPast = false,
  isCategoryPage = false,
  activity,
}: ActivityCardProps) {
  const router = useRouter();

  const activityFee = calculateActivityFee(activity) / 100;

  const handleActivityNavigation = () => {
    router.push(`/activity/${activity.id}`);
    // if (activity?.eventType === "COMPETITION") {
    //   router.push(`/competition/${activity.id}`);
    // } else {
    //   router.push(`/activity/${activity.id}`);
    // }
  };

  const renderActivityFee = () => {
    const fee = getActivityFee(activity) / 100;

    const totalFee =
      activity?.competitionCategory === "TEAM" &&
      activity.competitionDetail?.individualPays
        ? fee / activity?.competitionDetail?.groupSize! || 0
        : fee;

    return `${formatCurrency(totalFee, activity.currency)}`;
  };

  return (
    <div
      onClick={handleActivityNavigation}
      className="hover:cursor-pointer max-h-[380px] bg-white rounded-xl overflow-hidden shadow-lg border border-gray600/10"
    >
      <div className="relative">
        <Image
          src={scaleImageUrl(
            activity?.images[0] || activity?.societyAvatar,
            400,
            300
          )}
          alt={activity?.title || "Activity image"}
          width={400}
          height={300}
          className="w-full h-[200px] object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = "/assets/images/placeholder_image.webp";
          }}
        />

        {activity?.verified && (
          <p className="absolute top-4 left-4 text-xs bg-white p-1 px-2 text-gray500 rounded-3xl text-center font-medium flex items-center gap-1">
            <span>Verified</span>{" "}
            <BsFillPatchCheckFill size={10} className="text-green" />
          </p>
        )}

        {activity?.featured && (
          <p className="absolute top-4 left-4 text-xs bg-[#EFFAF6] p-1 px-2 text-green rounded-3xl text-center font-medium flex items-center gap-1">
            Featured
          </p>
        )}

        {activity?.sponsored && (
          <p className="absolute top-4 left-4 text-xs bg-[#FBDFB1] p-1 px-2 text-gray800 rounded-3xl text-center font-medium flex items-center gap-1">
            Sponsored
          </p>
        )}
      </div>
      {!isPast && (
        <div className="p-4 h-1/2">
          <span className="text-xs font-semibold text-gray600 uppercase">
            {activity?.category}
          </span>
          {isCategoryPage && (
            <span className="ml-3 text-xs bg-green200 p-1 px-3 text-green rounded-3xl text-center uppercase font-semibold">
              GUIDED
            </span>
          )}
          <h3 className="text-base font-semibold text-gray500 my-1 truncate">
            {activity?.title}
          </h3>
          <hr className="text-gray500/5" />
          {activity.eventType === "UPCOMING" && (
            <div className="flex items-center text-gray-600 text-xs mt-2">
              <LuCalendar className="w-3 h-3 mr-1" />
              <div className="flex items-center truncate">
                {formatDateWithoutDay(new Date(activity?.eventDate))} ·
                <span className="ml-1 truncate">
                  {" "}
                  {activity?.city} · {activity?.country}
                </span>
              </div>
            </div>
          )}
          <div className="flex items-center text-gray-600 text-xs mt-2">
            <IoCheckmarkDoneSharp className="text-black w-4 h-4 mr-1" />{" "}
            <span className="font-semibold">{activity?.totalGoing}</span>
            {activity.eventType === "UPCOMING" ? (
              <>&nbsp;people going&nbsp;</>
            ) : (
              <>&nbsp;people opted-in&nbsp;</>
            )}
            {activity?.ratings > 0 && (
              <>
                {" "}
                <FaStar className="text-[#F9A825] w-3 h-3 mx-1" />
                <span>{activity?.ratings} reviews</span>
              </>
            )}
          </div>
          {activity?.eventType !== "COMPETITION" && (
            <div className="flex items-center  text-gray500 font-semibold text-xs mt-2">
              {" "}
              <CiMoneyBill className="text-black w-4 h-4 mr-1" />{" "}
              {activityFee === 0
                ? "Free"
                : formatCurrency(activityFee, activity.currency || "NGN")}
            </div>
          )}

          {activity?.eventType === "COMPETITION" && (
            <div className="flex items-center  text-gray500 font-semibold text-xs mt-2">
              {" "}
              <CiMoneyBill className="text-black w-4 h-4 mr-1" />{" "}
              {activityFee === 0 ? "Free" : renderActivityFee()}
            </div>
          )}
        </div>
      )}

      {isPast && (
        <div className="p-4 h-1/2">
          <span className="text-xs font-semibold text-gray600 uppercase">
            {activity?.category}
          </span>
          <h3 className="text-base font-semibold text-gray500 my-1">
            {activity?.title}
          </h3>
          <hr className="text-gray500/5" />

          <div className="flex items-center text-gray-600 text-xs mt-2">
            <IoCheckmarkDoneSharp className="text-black w-4 h-4 mr-1" />{" "}
            <span className="font-semibold">
              {activity?.expectedAttendance}
            </span>
            &nbsp;people attended&nbsp;
            {activity?.ratings > 0 && (
              <>
                {" "}
                <FaStar className="text-[#F9A825] w-3 h-3 mx-1" />
                <span>{activity?.ratings} reviews</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
